import { Link } from '@tanstack/react-router'

import {
	SidebarMenuButton,
	SidebarMenuSubButton,
	useSidebar,
} from '@/components/ui/sidebar'

import type { LinkProps } from '@tanstack/react-router'
import type { ReactNode } from 'react'

/**
 * Wrapper to easily create sidebar links
 * @param {Linkprops['to']} to - Route to navigate to
 * @param {string} tooltip - The tooltip text
 * @param exact - Only highlight on an exact path match. Needed by a
 *   group's parent item, which would otherwise light up for every child route.
 * @param {ReactNode} children - The link content
 */
export function SidebarLink({
	to,
	tooltip,
	exact,
	children,
}: {
	to: LinkProps['to']
	tooltip: string
	exact?: boolean
	children: ReactNode
}) {
	const { setOpenMobile } = useSidebar()

	return (
		<SidebarMenuButton
			tooltip={tooltip}
			render={
				<Link
					to={to}
					className="data-active:text-sidebar-primary!"
					activeOptions={{ exact }}
					activeProps={{
						'data-active': true,
					}}
					onClick={() => setOpenMobile(false)}
				>
					{children}
				</Link>
			}
		/>
	)
}

/**
 * `SidebarLink`'s submenu counterpart — the ONE owner of the sub-entry link
 * shape (close the mobile sheet on navigate, the shared active colour, one
 * `data-active` mechanism), so the nested entries cannot drift from the rest
 * of the nav.
 *
 * Active has two modes:
 * - `isActive` omitted — the router decides (plain path match), the same
 *   `activeProps` idiom as `SidebarLink` above.
 * - `isActive` passed — the caller computed a rule the router cannot express
 *   (e.g. the dashboard entries' search-param + default-fallback rule); it is
 *   forwarded to `SidebarMenuSubButton`, whose own `isActive` contract renders
 *   the same `data-active`.
 */
export function SidebarSubLink({
	isActive,
	children,
	...linkProps
}: {
	isActive?: boolean
	children: ReactNode
} & Pick<LinkProps, 'to' | 'search'>) {
	const { setOpenMobile } = useSidebar()

	return (
		<SidebarMenuSubButton
			isActive={isActive ?? false}
			render={
				<Link
					{...linkProps}
					className="data-active:text-sidebar-primary!"
					{...(isActive === undefined
						? { activeProps: { 'data-active': true } as const }
						: {})}
					onClick={() => setOpenMobile(false)}
				>
					{children}
				</Link>
			}
		/>
	)
}
